import { getOpenCV } from "./opencv";

import { debugLog } from "../../../utils/debug";

export const DENSE_MATCH_STATUS = {
  NOT_EVALUATED: 0,
  ACCEPTED: 1,
  LOW_SCORE: 2,
  AMBIGUOUS: 3,
  REVERSE_REJECTED: 4,
  LEFT_RIGHT_REJECTED: 5,
};

// Zero-mean normalized cross-correlation for two grayscale patches.
function computeZNCC(dataA, dataB, width, xA, yA, xB, yB, blockRadius) {
  const diameter = blockRadius * 2 + 1;
  const sampleCount = diameter * diameter;

  let sumA = 0;
  let sumB = 0;

  for (let offsetY = -blockRadius; offsetY <= blockRadius; offsetY += 1) {
    const rowA = (yA + offsetY) * width;
    const rowB = (yB + offsetY) * width;

    for (let offsetX = -blockRadius; offsetX <= blockRadius; offsetX += 1) {
      sumA += dataA[rowA + xA + offsetX];
      sumB += dataB[rowB + xB + offsetX];
    }
  }

  const meanA = sumA / sampleCount;
  const meanB = sumB / sampleCount;

  let numerator = 0;
  let varianceA = 0;
  let varianceB = 0;

  for (let offsetY = -blockRadius; offsetY <= blockRadius; offsetY += 1) {
    const rowA = (yA + offsetY) * width;
    const rowB = (yB + offsetY) * width;

    for (let offsetX = -blockRadius; offsetX <= blockRadius; offsetX += 1) {
      const centeredA = dataA[rowA + xA + offsetX] - meanA;
      const centeredB = dataB[rowB + xB + offsetX] - meanB;

      numerator += centeredA * centeredB;
      varianceA += centeredA * centeredA;
      varianceB += centeredB * centeredB;
    }
  }

  const denominator = Math.sqrt(varianceA * varianceB);

  if (denominator <= 1e-12) {
    return -1;
  }

  return numerator / denominator;
}

// Fit a parabola around the winning ZNCC sample for subpixel disparity.
function refineSubpixelDisparity(
  scoreBefore,
  scoreCenter,
  scoreAfter,
  disparity,
) {
  if (
    !Number.isFinite(scoreBefore) ||
    !Number.isFinite(scoreCenter) ||
    !Number.isFinite(scoreAfter)
  ) {
    return disparity;
  }

  const denominator = scoreBefore - 2 * scoreCenter + scoreAfter;

  if (Math.abs(denominator) < 1e-12) {
    return disparity;
  }

  let offset = (0.5 * (scoreBefore - scoreAfter)) / denominator;

  // The fitted maximum should remain near the winning integer sample.
  offset = Math.max(-1, Math.min(1, offset));

  return disparity + offset;
}

// direction -1: xB = xA - d. direction +1: xA = xB + d.
function findBestDisparity(
  referenceData,
  targetData,
  width,
  height,
  x,
  y,
  options,
  diagnostics = null,
  resultStatus = null,
) {
  const {
    minDisparity,
    maxDisparity,
    blockRadius,
    minimumScore,
    ambiguityMargin,
    direction,
  } = options;

  let bestDisparity = Number.NaN;
  let bestScore = -Infinity;

  // Ignore adjacent samples when finding the competing correlation peak.
  const scores = [];

  for (
    let disparity = minDisparity;
    disparity <= maxDisparity;
    disparity += 1
  ) {
    const targetX = x + direction * disparity;

    if (targetX - blockRadius < 0 || targetX + blockRadius >= width) {
      continue;
    }

    const score = computeZNCC(
      referenceData,
      targetData,
      width,
      x,
      y,
      targetX,
      y,
      blockRadius,
    );

    scores.push({
      disparity,
      score,
    });

    if (score > bestScore) {
      bestScore = score;
      bestDisparity = disparity;
    }
  }

  if (!Number.isFinite(bestDisparity)) {
    if (diagnostics) {
      diagnostics.noCandidate += 1;
    }

    if (resultStatus) {
      resultStatus.reason = "noCandidate";
    }

    return null;
  }

  if (bestScore < minimumScore) {
    if (diagnostics) {
      diagnostics.lowScore += 1;
    }

    if (resultStatus) {
      resultStatus.reason = "lowScore";
    }

    return null;
  }

  const peakExclusionRadius = 2;

  let secondBestScore = -Infinity;

  for (const candidate of scores) {
    if (
      Math.abs(candidate.disparity - bestDisparity) > peakExclusionRadius &&
      candidate.score > secondBestScore
    ) {
      secondBestScore = candidate.score;
    }
  }

  if (
    Number.isFinite(secondBestScore) &&
    bestScore - secondBestScore < ambiguityMargin
  ) {
    if (diagnostics) {
      diagnostics.ambiguous += 1;
    }

    if (resultStatus) {
      resultStatus.reason = "ambiguous";
    }

    return null;
  }

  if (diagnostics) {
    diagnostics.accepted += 1;
  }

  if (resultStatus) {
    resultStatus.reason = "accepted";
  }

  let scoreBefore = Number.NaN;
  let scoreAfter = Number.NaN;

  for (const candidate of scores) {
    if (candidate.disparity === bestDisparity - 1) {
      scoreBefore = candidate.score;
    } else if (candidate.disparity === bestDisparity + 1) {
      scoreAfter = candidate.score;
    }
  }

  const refinedDisparity = refineSubpixelDisparity(
    scoreBefore,
    bestScore,
    scoreAfter,
    bestDisparity,
  );

  const ambiguity = Number.isFinite(secondBestScore)
    ? bestScore - secondBestScore
    : 0;

  return {
    disparity: refinedDisparity,
    integerDisparity: bestDisparity,
    score: bestScore,
    secondBestScore,
    ambiguity,
  };
}

// Dense rectified stereo using ZNCC, ambiguity rejection, and left-right consistency.
export async function computeDenseDisparity(
  imageElementA,
  imageElementB,
  options = {},
) {
  const cv = await getOpenCV();

  const {
    minDisparity = 1,
    maxDisparity = 256,
    blockRadius = 3,
    minimumScore = 0.75,
    ambiguityMargin = 0.015,
    leftRightThreshold = 1,
    pixelStep = 1,
  } = options;

  const sourceA = cv.imread(imageElementA);
  const sourceB = cv.imread(imageElementB);

  const grayA = new cv.Mat();
  const grayB = new cv.Mat();

  try {
    cv.cvtColor(sourceA, grayA, cv.COLOR_RGBA2GRAY);
    cv.cvtColor(sourceB, grayB, cv.COLOR_RGBA2GRAY);

    if (grayA.cols !== grayB.cols || grayA.rows !== grayB.rows) {
      throw new Error("Stereo images must have identical dimensions.");
    }

    const width = grayA.cols;
    const height = grayA.rows;

    const disparities = new Float32Array(width * height);
    const confidence = new Float32Array(width * height);
    const matchStatus = new Uint8Array(width * height);

    disparities.fill(Number.NaN);
    confidence.fill(Number.NaN);

    const dataA = grayA.data;
    const dataB = grayB.data;

    let candidateCount = 0;
    let forwardAccepted = 0;
    let consistencyAccepted = 0;
    let subpixelCount = 0;

    const forwardDiagnostics = {
      noCandidate: 0,
      lowScore: 0,
      ambiguous: 0,
      accepted: 0,
    };

    const reverseDiagnostics = {
      noCandidate: 0,
      lowScore: 0,
      ambiguous: 0,
      accepted: 0,
    };

    let leftRightRejected = 0;

    const minimumY = blockRadius;
    const maximumY = height - blockRadius;

    // Keep both patches in bounds for xB = xA - disparity.
    const minimumX = blockRadius + Math.max(0, minDisparity);
    const maximumX = width - blockRadius - Math.max(0, -maxDisparity);

    for (let y = minimumY; y < maximumY; y += pixelStep) {
      for (let x = minimumX; x < maximumX; x += pixelStep) {
        candidateCount += 1;

        const index = y * width + x;

        const forwardStatus = {
          reason: null,
        };

        const forward = findBestDisparity(
          dataA,
          dataB,
          width,
          height,
          x,
          y,
          {
            minDisparity,
            maxDisparity,
            blockRadius,
            minimumScore,
            ambiguityMargin,
            direction: -1,
          },
          forwardDiagnostics,
          forwardStatus,
        );

        if (!forward) {
          if (forwardStatus.reason === "lowScore") {
            matchStatus[index] = DENSE_MATCH_STATUS.LOW_SCORE;
          } else if (forwardStatus.reason === "ambiguous") {
            matchStatus[index] = DENSE_MATCH_STATUS.AMBIGUOUS;
          }

          continue;
        }

        forwardAccepted += 1;

        // Reverse matching starts from the winning integer sample.
        const matchedX = x - forward.integerDisparity;

        const reverseStatus = {
          reason: null,
        };

        const reverse = findBestDisparity(
          dataB,
          dataA,
          width,
          height,
          matchedX,
          y,
          {
            minDisparity,
            maxDisparity,
            blockRadius,
            minimumScore,
            ambiguityMargin,
            direction: 1,
          },
          reverseDiagnostics,
          reverseStatus,
        );

        if (!reverse) {
          matchStatus[index] = DENSE_MATCH_STATUS.REVERSE_REJECTED;
          continue;
        }

        if (
          Math.abs(forward.disparity - reverse.disparity) > leftRightThreshold
        ) {
          leftRightRejected += 1;
          matchStatus[index] = DENSE_MATCH_STATUS.LEFT_RIGHT_REJECTED;
          continue;
        }

        disparities[index] = forward.disparity;

        // Confidence combines ZNCC score with peak separation.
        const normalizedAmbiguity = Math.max(
          0,
          Math.min(1, forward.ambiguity / 0.25),
        );

        confidence[index] = forward.score * normalizedAmbiguity;
        matchStatus[index] = DENSE_MATCH_STATUS.ACCEPTED;

        consistencyAccepted += 1;

        if (
          Number.isFinite(forward.disparity) &&
          Math.abs(forward.disparity - forward.integerDisparity) > 1e-6
        ) {
          subpixelCount += 1;
        }
      }
    }

    debugLog("[Reconstruction] Dense stereo:", {
      width,
      height,
      candidateCount,
      forwardAccepted,
      consistencyAccepted,
      subpixelCount,
      forwardDiagnostics,
      reverseDiagnostics,
      leftRightRejected,
      minDisparity,
      maxDisparity,
      blockRadius,
      minimumScore,
      ambiguityMargin,
      leftRightThreshold,
      pixelStep,
    });

    return {
      width,
      height,
      disparities,
      confidence,
      matchStatus,
      validCount: consistencyAccepted,
      options: {
        minDisparity,
        maxDisparity,
        blockRadius,
        minimumScore,
        ambiguityMargin,
        leftRightThreshold,
        pixelStep,
      },
    };
  } finally {
    sourceA.delete();
    sourceB.delete();
    grayA.delete();
    grayB.delete();
  }
}

export function densifySparseDisparity(
  disparity,
  { radius = 3, minimumSupport = 3, maximumSpread = 2, maximumPasses = 2 } = {},
) {
  if (!disparity) {
    return null;
  }

  const { width, height } = disparity;

  let currentDisparities = new Float32Array(disparity.disparities);
  let currentConfidence = new Float32Array(disparity.confidence);

  let initialCount = 0;

  for (let i = 0; i < currentDisparities.length; i += 1) {
    if (Number.isFinite(currentDisparities[i])) {
      initialCount += 1;
    }
  }

  let totalAdded = 0;

  for (let pass = 0; pass < maximumPasses; pass += 1) {
    const nextDisparities = new Float32Array(currentDisparities);
    const nextConfidence = new Float32Array(currentConfidence);

    let addedThisPass = 0;

    for (let y = radius; y < height - radius; y += 1) {
      for (let x = radius; x < width - radius; x += 1) {
        const index = y * width + x;

        if (Number.isFinite(currentDisparities[index])) {
          continue;
        }

        const neighbors = [];

        for (let offsetY = -radius; offsetY <= radius; offsetY += 1) {
          for (let offsetX = -radius; offsetX <= radius; offsetX += 1) {
            if (offsetX === 0 && offsetY === 0) {
              continue;
            }

            const neighborIndex = (y + offsetY) * width + (x + offsetX);

            const neighborDisparity = currentDisparities[neighborIndex];

            if (Number.isFinite(neighborDisparity)) {
              neighbors.push(neighborDisparity);
            }
          }
        }

        if (neighbors.length < minimumSupport) {
          continue;
        }

        neighbors.sort((a, b) => a - b);

        const minimum = neighbors[0];
        const maximum = neighbors[neighbors.length - 1];

        if (maximum - minimum > maximumSpread) {
          continue;
        }

        const middle = Math.floor(neighbors.length / 2);

        const median =
          neighbors.length % 2 === 0
            ? (neighbors[middle - 1] + neighbors[middle]) / 2
            : neighbors[middle];

        nextDisparities[index] = median;
        nextConfidence[index] = 0.1;

        addedThisPass += 1;
      }
    }

    currentDisparities = nextDisparities;
    currentConfidence = nextConfidence;

    totalAdded += addedThisPass;

    if (addedThisPass === 0) {
      break;
    }
  }

  debugLog("[Reconstruction] Sparse disparity densification:", {
    initial: initialCount,
    added: totalAdded,
    total: initialCount + totalAdded,
    radius,
    minimumSupport,
    maximumSpread,
    maximumPasses,
  });

  return {
    ...disparity,
    disparities: currentDisparities,
    confidence: currentConfidence,
    validCount: initialCount + totalAdded,
  };
}

export function filterDenseDisparity(
  disparity,
  {
    radius = 2,
    disparityThreshold = 2.0,
    minimumSupport = 3,
    minimumConfidence = 0.25,
  } = {},
) {
  if (!disparity) {
    return null;
  }

  const { width, height, disparities, confidence } = disparity;

  const filteredDisparities = new Float32Array(disparities.length);
  const filteredConfidence = new Float32Array(disparities.length);

  filteredDisparities.fill(Number.NaN);
  filteredConfidence.fill(Number.NaN);

  let inputCount = 0;
  let acceptedCount = 0;
  let lowConfidenceRejected = 0;
  let isolatedRejected = 0;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = y * width + x;
      const centerDisparity = disparities[index];

      if (!Number.isFinite(centerDisparity)) {
        continue;
      }

      inputCount += 1;

      const centerConfidence =
        confidence && Number.isFinite(confidence[index])
          ? confidence[index]
          : 1;

      if (centerConfidence < minimumConfidence) {
        lowConfidenceRejected += 1;
        continue;
      }

      let support = 0;

      const minY = Math.max(0, y - radius);
      const maxY = Math.min(height - 1, y + radius);
      const minX = Math.max(0, x - radius);
      const maxX = Math.min(width - 1, x + radius);

      for (let neighborY = minY; neighborY <= maxY; neighborY += 1) {
        for (let neighborX = minX; neighborX <= maxX; neighborX += 1) {
          if (neighborX === x && neighborY === y) {
            continue;
          }

          const neighborIndex = neighborY * width + neighborX;
          const neighborDisparity = disparities[neighborIndex];

          if (!Number.isFinite(neighborDisparity)) {
            continue;
          }

          if (
            Math.abs(neighborDisparity - centerDisparity) <= disparityThreshold
          ) {
            support += 1;

            if (support >= minimumSupport) {
              break;
            }
          }
        }

        if (support >= minimumSupport) {
          break;
        }
      }

      if (support < minimumSupport) {
        isolatedRejected += 1;
        continue;
      }

      filteredDisparities[index] = centerDisparity;
      filteredConfidence[index] = centerConfidence;
      acceptedCount += 1;
    }
  }

  debugLog("[Reconstruction] Dense disparity filtering:", {
    input: inputCount,
    accepted: acceptedCount,
    rejected: inputCount - acceptedCount,
    lowConfidenceRejected,
    isolatedRejected,
    radius,
    disparityThreshold,
    minimumSupport,
    minimumConfidence,
  });

  return {
    ...disparity,
    disparities: filteredDisparities,
    confidence: filteredConfidence,
    validCount: acceptedCount,
  };
}

export async function propagateDenseDisparity(
  imageElementA,
  imageElementB,
  disparity,
  {
    supportRadius = 3,
    minimumSupport = 3,
    maximumSupportSpread = 2,
    searchRadius = 2,
    minimumScore = 0.7,
    ambiguityMargin = 0.01,
    leftRightThreshold = 1,
  } = {},
) {
  if (!disparity) {
    return null;
  }

  const cv = await getOpenCV();

  const sourceA = cv.imread(imageElementA);
  const sourceB = cv.imread(imageElementB);

  const grayA = new cv.Mat();
  const grayB = new cv.Mat();

  try {
    cv.cvtColor(sourceA, grayA, cv.COLOR_RGBA2GRAY);
    cv.cvtColor(sourceB, grayB, cv.COLOR_RGBA2GRAY);

    if (grayA.cols !== grayB.cols || grayA.rows !== grayB.rows) {
      throw new Error("Stereo images must have identical dimensions.");
    }

    const { width, height, disparities, confidence, options = {} } = disparity;

    if (grayA.cols !== width || grayA.rows !== height) {
      throw new Error(
        "Stereo images do not match the disparity map dimensions.",
      );
    }

    const blockRadius = options.blockRadius ?? 3;
    const minDisparity = options.minDisparity ?? 1;
    const maxDisparity = options.maxDisparity ?? 256;

    const dataA = grayA.data;
    const dataB = grayB.data;

    const propagatedDisparities = new Float32Array(disparities);
    const propagatedConfidence = new Float32Array(confidence);

    let existingCount = 0;
    let proposalCount = 0;
    let propagatedCount = 0;

    let insufficientSupportCount = 0;
    let excessiveSpreadCount = 0;
    let forwardRejectedCount = 0;
    let reverseRejectedCount = 0;
    let leftRightRejectedCount = 0;

    for (let i = 0; i < disparities.length; i += 1) {
      if (Number.isFinite(disparities[i])) {
        existingCount += 1;
      }
    }

    const minimumY = Math.max(blockRadius, supportRadius);
    const maximumY = height - Math.max(blockRadius, supportRadius);

    const minimumX =
      Math.max(blockRadius, supportRadius) + Math.max(0, minDisparity);

    const maximumX =
      width - Math.max(blockRadius, supportRadius) - Math.max(0, -maxDisparity);

    for (let y = minimumY; y < maximumY; y += 1) {
      for (let x = minimumX; x < maximumX; x += 1) {
        const index = y * width + x;

        if (Number.isFinite(disparities[index])) {
          continue;
        }

        const neighbors = [];

        for (
          let offsetY = -supportRadius;
          offsetY <= supportRadius;
          offsetY += 1
        ) {
          for (
            let offsetX = -supportRadius;
            offsetX <= supportRadius;
            offsetX += 1
          ) {
            if (offsetX === 0 && offsetY === 0) {
              continue;
            }

            const neighborIndex = (y + offsetY) * width + (x + offsetX);

            const neighborDisparity = disparities[neighborIndex];

            if (Number.isFinite(neighborDisparity)) {
              neighbors.push(neighborDisparity);
            }
          }
        }

        if (neighbors.length < minimumSupport) {
          insufficientSupportCount += 1;
          continue;
        }

        neighbors.sort((a, b) => a - b);

        const minimumNeighbor = neighbors[0];
        const maximumNeighbor = neighbors[neighbors.length - 1];

        if (maximumNeighbor - minimumNeighbor > maximumSupportSpread) {
          excessiveSpreadCount += 1;
          continue;
        }

        const middle = Math.floor(neighbors.length / 2);

        const proposedDisparity =
          neighbors.length % 2 === 0
            ? (neighbors[middle - 1] + neighbors[middle]) / 2
            : neighbors[middle];

        proposalCount += 1;

        // Neighbor disparities constrain the local ZNCC search.
        const localMinDisparity = Math.max(
          minDisparity,
          Math.floor(proposedDisparity - searchRadius),
        );

        const localMaxDisparity = Math.min(
          maxDisparity,
          Math.ceil(proposedDisparity + searchRadius),
        );

        if (localMinDisparity > localMaxDisparity) {
          continue;
        }

        const forward = findBestDisparity(dataA, dataB, width, height, x, y, {
          minDisparity: localMinDisparity,
          maxDisparity: localMaxDisparity,
          blockRadius,
          minimumScore,
          ambiguityMargin,
          direction: -1,
        });

        if (!forward) {
          forwardRejectedCount += 1;
          continue;
        }

        const matchedX = x - forward.integerDisparity;

        if (matchedX - blockRadius < 0 || matchedX + blockRadius >= width) {
          reverseRejectedCount += 1;
          continue;
        }

        // Use the same local interval for the reverse consistency check.
        const reverse = findBestDisparity(
          dataB,
          dataA,
          width,
          height,
          matchedX,
          y,
          {
            minDisparity: localMinDisparity,
            maxDisparity: localMaxDisparity,
            blockRadius,
            minimumScore,
            ambiguityMargin,
            direction: 1,
          },
        );

        if (!reverse) {
          reverseRejectedCount += 1;
          continue;
        }

        if (
          Math.abs(forward.disparity - reverse.disparity) > leftRightThreshold
        ) {
          leftRightRejectedCount += 1;
          continue;
        }

        propagatedDisparities[index] = forward.disparity;

        // Propagated matches retain conservative confidence.
        const normalizedAmbiguity = Math.max(
          0,
          Math.min(1, forward.ambiguity / 0.25),
        );

        const matchConfidence = forward.score * normalizedAmbiguity;

        propagatedConfidence[index] = Math.min(0.5, matchConfidence);

        propagatedCount += 1;
      }
    }

    debugLog("[Reconstruction] Dense disparity propagation:", {
      existing: existingCount,
      proposals: proposalCount,
      propagated: propagatedCount,
      total: existingCount + propagatedCount,
      insufficientSupport: insufficientSupportCount,
      excessiveSupportSpread: excessiveSpreadCount,
      forwardRejected: forwardRejectedCount,
      reverseRejected: reverseRejectedCount,
      leftRightRejected: leftRightRejectedCount,
      supportRadius,
      minimumSupport,
      maximumSupportSpread,
      searchRadius,
      minimumScore,
      ambiguityMargin,
      leftRightThreshold,
    });

    return {
      ...disparity,
      disparities: propagatedDisparities,
      confidence: propagatedConfidence,
      validCount: existingCount + propagatedCount,
      propagatedCount,
    };
  } finally {
    sourceA.delete();
    sourceB.delete();
    grayA.delete();
    grayB.delete();
  }
}

export function fillDenseDisparityHoles(
  disparity,
  {
    radius = 2,
    minimumSupport = 5,
    maximumSpread = 1.5,
    minimumDirectionalSupport = 1,
  } = {},
) {
  if (!disparity) {
    return null;
  }

  const { width, height, disparities, confidence } = disparity;

  const filledDisparities = new Float32Array(disparities);
  const filledConfidence = new Float32Array(confidence);

  let originalCount = 0;
  let filledCount = 0;

  for (let i = 0; i < disparities.length; i += 1) {
    if (Number.isFinite(disparities[i])) {
      originalCount += 1;
    }
  }

  for (let y = radius; y < height - radius; y += 1) {
    for (let x = radius; x < width - radius; x += 1) {
      const index = y * width + x;

      if (Number.isFinite(disparities[index])) {
        continue;
      }

      const neighbors = [];

      let leftSupport = 0;
      let rightSupport = 0;
      let topSupport = 0;
      let bottomSupport = 0;

      for (let offset = 1; offset <= radius; offset += 1) {
        const left = disparities[y * width + (x - offset)];
        const right = disparities[y * width + (x + offset)];
        const top = disparities[(y - offset) * width + x];
        const bottom = disparities[(y + offset) * width + x];

        if (Number.isFinite(left)) {
          neighbors.push(left);
          leftSupport += 1;
        }

        if (Number.isFinite(right)) {
          neighbors.push(right);
          rightSupport += 1;
        }

        if (Number.isFinite(top)) {
          neighbors.push(top);
          topSupport += 1;
        }

        if (Number.isFinite(bottom)) {
          neighbors.push(bottom);
          bottomSupport += 1;
        }
      }

      if (neighbors.length < minimumSupport) {
        continue;
      }

      const hasHorizontalSupport =
        leftSupport >= minimumDirectionalSupport &&
        rightSupport >= minimumDirectionalSupport;

      const hasVerticalSupport =
        topSupport >= minimumDirectionalSupport &&
        bottomSupport >= minimumDirectionalSupport;

      if (!hasHorizontalSupport && !hasVerticalSupport) {
        continue;
      }

      neighbors.sort((a, b) => a - b);

      const minimum = neighbors[0];
      const maximum = neighbors[neighbors.length - 1];

      if (maximum - minimum > maximumSpread) {
        continue;
      }

      const middle = Math.floor(neighbors.length / 2);

      const median =
        neighbors.length % 2 === 0
          ? (neighbors[middle - 1] + neighbors[middle]) / 2
          : neighbors[middle];

      filledDisparities[index] = median;

      // Keep interpolated samples lower-confidence than observed matches.
      filledConfidence[index] = 0.05;

      filledCount += 1;
    }
  }

  debugLog("[Reconstruction] Dense disparity hole filling:", {
    original: originalCount,
    filled: filledCount,
    total: originalCount + filledCount,
    radius,
    minimumSupport,
    maximumSpread,
  });

  return {
    ...disparity,
    disparities: filledDisparities,
    confidence: filledConfidence,
    validCount: originalCount + filledCount,
  };
}

// Back-project rectified disparity: Z = fB/(d + doffs), X = (u-cx)Z/fx, Y = (v-cy)Z/fy.
export function disparityToPointCloud(
  disparityResult,
  calibration,
  imageData = null,
) {
  const { width, height, disparities, confidence } = disparityResult;

  const K = calibration.cameraMatrixA;

  const fx = K.get(0, 0);
  const fy = K.get(1, 1);
  const cx = K.get(0, 2);
  const cy = K.get(1, 2);

  const baseline = calibration.baseline;
  const disparityOffset = calibration.disparityOffset ?? 0;

  const points = [];

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = y * width + x;
      const disparity = disparities[index];

      if (!Number.isFinite(disparity)) {
        continue;
      }

      const correctedDisparity = disparity + disparityOffset;

      const depthDisparity = calibration.useAbsoluteDisparity
        ? Math.abs(correctedDisparity)
        : correctedDisparity;

      if (depthDisparity <= 0) {
        continue;
      }

      const z = (fx * baseline) / depthDisparity;

      // Two-view depth becomes unstable as disparity approaches zero.
      const maximumDepth = 80;

      // if (!Number.isFinite(z) || z <= 0 || z > maximumDepth) {
      //   continue;
      // }

      if (!Number.isFinite(z) || z <= 0) {
        continue;
      }

      const pointX = ((x - cx) * z) / fx;
      const pointY = ((y - cy) * z) / fy;

      if (
        !Number.isFinite(pointX) ||
        !Number.isFinite(pointY) ||
        !Number.isFinite(z)
      ) {
        continue;
      }

      let intensity = 1;

      if (
        imageData &&
        imageData.width === width &&
        imageData.height === height
      ) {
        const pixelIndex = index * 4;

        const r = imageData.data[pixelIndex];
        const g = imageData.data[pixelIndex + 1];
        const b = imageData.data[pixelIndex + 2];

        intensity = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
      }

      points.push({
        x: pointX,
        y: pointY,
        z,
        u: x,
        v: y,
        disparity,
        confidence: confidence?.[index] ?? 1,
        intensity,
      });
    }
  }

  return {
    width,
    height,
    points,
  };
}
